// PickUp.js
// Pick Up Draft hub — web port of PickUpOverviewView.swift.
//
// Layout mirrors iOS:
//   • Nav bar (back + wordmark) with a friends icon pinned top-right (yellow dot when there
//     are incoming friend requests → opens Friends)
//   • Title "PICK-UP DRAFT" flanked by the strategy glyph (teal)
//   • Sport selector: 5 icon tiles; selected fills with the sport color; a dot appears
//     top-right — teal for an incoming challenge in that sport, yellow when it's your turn
//   • Record card: RECORD (W–L) | WIN %
//   • Scrollable middle: CHALLENGES (incoming/sent), then ACTIVE DRAFTS (shared ActiveGameRow),
//     or a HOW TO PLAY rules card when both are empty
//   • Pinned bottom: CHALLENGE FRIEND (opens a friend picker) + FIND OPPONENT (→ matchmaking)
//
// Teal accent (#00BFB8) matches the iOS pickUpAccent and the Roster Battles card.

import React, { useState, useEffect, useCallback, useRef } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../managers/AuthContext";
import {
  listenToIncomingChallenges,
  listenToSentChallenges,
  acceptFriendChallenge,
  declineFriendChallenge,
  cancelFriendChallenge,
  sendFriendChallenge,
  callableErrorMessage,
  loadVersusRecords,
  isUserTurn,
} from "../managers/pickupService";
import { loadActivePickUpGames } from "../managers/matchmakingService";
import { listenToFriends, listenToIncomingRequests } from "../managers/friendsService";
import ActiveGameRow, { SPORT_META, SPORT_ORDER, maskStyle } from "../components/ActiveGameRow";
import strategyIcon from "../assets/strategy-fill.svg";
import { DIFFICULTIES } from "../managers/cpuOpponent";
import { fetchPlayersForSport } from "../managers/playerService";

const APP_THEME = "#d4f53c";
const TEAL = "#00BFB8";
const REFRESH_MS = 30000;

const isLive = (g) => g.status === "waiting" || g.status === "active";
const isDone = (g) => g.status === "complete" || g.status === "forfeited";

export default function PickUp() {
  const { user } = useAuth();
  const uid = user?.uid || null;
  const navigate = useNavigate();

  const [selectedSport, setSelectedSport] = useState("football");
  const [incoming, setIncoming] = useState([]);
  const [sent, setSent] = useState([]);
  const [games, setGames] = useState([]);
  const [records, setRecords] = useState({});
  const [friends, setFriends] = useState([]);
  const [friendRequests, setFriendRequests] = useState([]);
  const [challengeError, setChallengeError] = useState("");
  const [accepting, setAccepting] = useState(null);
  const [showPicker, setShowPicker] = useState(false);
  const [showCpuPicker, setShowCpuPicker] = useState(false);

  const errTimer = useRef(null);
  const showError = useCallback((m) => {
    setChallengeError(m);
    if (errTimer.current) window.clearTimeout(errTimer.current);
    errTimer.current = window.setTimeout(() => setChallengeError(""), 4000);
  }, []);

  const refreshGames = useCallback(async () => {
    if (!uid) return;
    try {
      setGames(await loadActivePickUpGames(uid));
    } catch (_) {
      /* keep last good list */
    }
  }, [uid]);

  useEffect(() => {
    if (!uid) return undefined;
    const unsubIn = listenToIncomingChallenges(uid, setIncoming);
    const unsubSent = listenToSentChallenges(uid, setSent);
    const unsubFriends = listenToFriends(uid, setFriends);
    const unsubReq = listenToIncomingRequests(uid, setFriendRequests);
    loadVersusRecords(uid).then(setRecords).catch(() => {});
    refreshGames();
    const interval = window.setInterval(refreshGames, REFRESH_MS);
    return () => {
      unsubIn();
      unsubSent();
      unsubFriends();
      unsubReq();
      window.clearInterval(interval);
    };
  }, [uid, refreshGames]);

  // Pre-warm the player list for the selected sport. A solo CPU game builds its player pools
  // on-device (unlike a real game, whose pools arrive pre-built from the server), so it needs
  // the full player list downloaded first. Kicking that download off the moment a sport is
  // selected means it's usually finished by the time the user reaches the draft. This reuses
  // playerService's in-memory cache, so it's a no-op if the sport is already loaded and never
  // downloads the same sport twice.
  useEffect(() => {
    fetchPlayersForSport(selectedSport).catch(() => {});
  }, [selectedSport]);

  // Per-sport cues for the selector dots.
  const challengeSports = new Set(incoming.map((c) => c.sport));
  const yourTurnSports = new Set(games.filter((g) => isLive(g) && isUserTurn(g, uid)).map((g) => g.sport));

  const incomingForSport = incoming.filter((c) => c.sport === selectedSport);
  const sentForSport = sent.filter((c) => c.sport === selectedSport);
  const hasChallenges = incomingForSport.length > 0 || sentForSport.length > 0;

  // Live (your-turn first, then most recent) followed by completed-unviewed.
  const liveForSport = games
    .filter((g) => g.sport === selectedSport && isLive(g))
    .sort((a, b) => {
      const at = isUserTurn(a, uid), bt = isUserTurn(b, uid);
      if (at !== bt) return at ? -1 : 1;
      return b.lastActionAt.getTime() - a.lastActionAt.getTime();
    });
  const doneForSport = games
    .filter((g) => g.sport === selectedSport && isDone(g))
    .sort((a, b) => b.lastActionAt.getTime() - a.lastActionAt.getTime());
  const gamesForSport = [...liveForSport, ...doneForSport];

  const isEmpty = !hasChallenges && gamesForSport.length === 0;
  const meta = SPORT_META[selectedSport];
  const rec = records[selectedSport] || { wins: 0, losses: 0 };
  const games_ = rec.wins + rec.losses;
  const winPct = games_ > 0 ? `${((rec.wins / games_) * 100).toFixed(1)}%` : "—";

  const accept = async (c) => {
    if (accepting) return;
    setAccepting(c.id);
    try {
      const { gameID } = await acceptFriendChallenge(c.id);
      navigate(`/battles/pickup/draft?gameID=${gameID}`);
    } catch (e) {
      setAccepting(null);
      showError(callableErrorMessage(e));
    }
  };
  const decline = async (c) => {
    try { await declineFriendChallenge(c.id); } catch (e) { showError(callableErrorMessage(e)); }
  };
  const cancel = async (c) => {
    try { await cancelFriendChallenge(c.id); } catch (e) { showError(callableErrorMessage(e)); }
  };
  const challengeFriend = async (f) => {
    setShowPicker(false);
    try {
      await sendFriendChallenge({ toUID: f.id, toUsername: f.username, sportId: selectedSport });
    } catch (e) {
      showError(callableErrorMessage(e));
    }
  };

  const openGame = (g) =>
    navigate(isDone(g) ? `/battles/pickup/complete?gameID=${g.id}` : `/battles/pickup/draft?gameID=${g.id}`);

  if (!uid) {
    return (
      <div style={styles.page}>
        <TitleRow />
        <div style={styles.rulesCard}>
          <p style={styles.dim}>Pick Up Draft needs a Rosterable account.</p>
          <button style={styles.primaryBtn} onClick={() => navigate("/login")}>SIGN IN</button>
        </div>
      </div>
    );
  }

  return (
    <div style={styles.page}>
      {/* Header — back, centered title, friends icon, on one row. The app wordmark is
          already shown by the shell's top bar, so there's no separate nav band. */}
      <div style={styles.headerRow}>
        <button style={styles.iconBtn} onClick={() => navigate("/battles")}>‹</button>
        <div style={styles.titleCenter}>
          <span style={maskStyle(strategyIcon, TEAL, 22)} />
          <span style={styles.title}>PICK-UP DRAFT</span>
          <span style={maskStyle(strategyIcon, TEAL, 22)} />
        </div>
        <button style={styles.friendsBtn} onClick={() => navigate("/battles/pickup/friends")} title="Friends">
          <span style={{ position: "relative", display: "inline-block", lineHeight: 0 }}>
            <svg width="22" height="22" viewBox="0 0 24 24" fill="none"
                 stroke={friendRequests.length ? APP_THEME : "rgba(255,255,255,0.4)"} strokeWidth="1.8"
                 strokeLinecap="round" strokeLinejoin="round">
              <circle cx="9" cy="8" r="3.2" />
              <path d="M3.5 19c0-3 2.5-5 5.5-5s5.5 2 5.5 5" />
              <path d="M17 7.5a2.6 2.6 0 0 1 0 5" />
              <path d="M18 14c2.2.4 3.8 2.2 3.8 4.6" />
            </svg>
            {friendRequests.length > 0 && <span style={styles.friendsDot} />}
          </span>
        </button>
      </div>

      {/* Sport selector */}
      <div style={styles.sportRow}>
        {SPORT_ORDER.map((id) => {
          const sm = SPORT_META[id];
          const selected = id === selectedSport;
          const dot = challengeSports.has(id) ? TEAL : yourTurnSports.has(id) ? APP_THEME : null;
          return (
            <button
              key={id}
              onClick={() => setSelectedSport(id)}
              style={{
                ...styles.sportTile,
                background: selected ? sm.hex : "rgba(255,255,255,0.07)",
                border: selected ? "none" : "1px solid rgba(255,255,255,0.12)",
                boxShadow: selected ? `0 4px 8px ${sm.hex}73` : "none",
                transform: selected ? "scale(1.04)" : "scale(1)",
              }}
            >
              <span style={maskStyle(sm.icon, selected ? "#0c0d10" : "rgba(255,255,255,0.7)", 24)} />
              {dot && <span style={{ ...styles.sportDot, background: dot }} />}
            </button>
          );
        })}
      </div>

      {/* Record card */}
      <div style={{ ...styles.recordCard, background: `${meta.hex}1a`, borderColor: `${meta.hex}59` }}>
        <div style={styles.recordCol}>
          <span style={styles.recordValue}>{rec.wins}-{rec.losses}</span>
          <span style={styles.recordLabel}>RECORD</span>
        </div>
        <span style={styles.recordDivider} />
        <div style={styles.recordCol}>
          <span style={styles.recordValue}>{winPct}</span>
          <span style={styles.recordLabel}>WIN %</span>
        </div>
      </div>

      {/* Scrollable middle */}
      <div style={styles.scroll}>
        {hasChallenges && (
          <div style={styles.block}>
            <SectionHeader label="CHALLENGES" color={meta.hex} />
            {incomingForSport.map((c) => (
              <IncomingChallengeRow
                key={c.id}
                challenge={c}
                hex={meta.hex}
                accepting={accepting === c.id}
                onAccept={() => accept(c)}
                onDecline={() => decline(c)}
              />
            ))}
            {sentForSport.map((c) => (
              <SentChallengeRow key={c.id} challenge={c} onCancel={() => cancel(c)} />
            ))}
          </div>
        )}

        {gamesForSport.length > 0 && (
          <div style={styles.block}>
            <SectionHeader label="ACTIVE DRAFTS" color={meta.hex} />
            <div style={styles.gameList}>
              {gamesForSport.map((g) => (
                <ActiveGameRow
                  key={g.id}
                  game={g}
                  currentUserID={uid}
                  isCompleted={isDone(g)}
                  onTap={() => openGame(g)}
                />
              ))}
            </div>
          </div>
        )}

        {isEmpty && (
          <div style={styles.rulesCard}>
            <SectionHeader label="HOW TO PLAY" color={meta.hex} />
            <Rule hex={meta.hex} text="Head to head drafts vs an opponent" />
            <Rule hex={meta.hex} text="Draft using the same pool of available players" />
            <Rule hex={meta.hex} text="Snake draft order" />
            <Rule hex={meta.hex} text="24 hour max timer per pick" />
            <Rule hex={meta.hex} text="Forfeit on consecutive missed picks" />
          </div>
        )}

        {challengeError && <p style={styles.error}>{challengeError}</p>}
      </div>

      {/* Pinned buttons — PLAY CPU (solo vs computer) on top, the two head-to-head
          options side by side below it. */}
      <div style={styles.pinned}>
        <button
          style={{ ...styles.cpuBtn, background: `linear-gradient(to right, ${meta.hex}, ${meta.hex}cc)`, boxShadow: `0 5px 10px ${meta.hex}66` }}
          onClick={() => setShowCpuPicker(true)}
        >
          PLAY CPU
        </button>
        <div style={styles.pinnedRow}>
          <button
            style={styles.tealBtn}
            onClick={() => (friends.length === 0 ? navigate("/battles/pickup/friends") : setShowPicker(true))}
          >
            {friends.length === 0 ? "ADD FRIENDS" : "CHALLENGE FRIEND"}
          </button>
          <button
            style={styles.tealBtn}
            onClick={() => navigate(`/battles/pickup/matchmaking?sport=${selectedSport}`)}
          >
            FIND OPPONENT
          </button>
        </div>
      </div>

      {/* Friend picker — challenge a friend in the selected sport (mirrors FriendPickerSheet) */}
      {showPicker && (
        <div style={styles.overlay} onClick={() => setShowPicker(false)}>
          <div style={styles.sheet} onClick={(e) => e.stopPropagation()}>
            <div style={styles.pickerHeader}>
              <span style={{ width: 28 }} />
              <div style={styles.pickerTitleWrap}>
                <span style={styles.pickerTitle}>CHALLENGE A FRIEND</span>
                <span style={styles.pickerSportRow}>
                  <span style={maskStyle(meta.icon, meta.hex, 12)} />
                  <span style={{ ...styles.pickerSportName, color: meta.hex }}>{meta.label.toUpperCase()}</span>
                </span>
              </div>
              <button style={styles.pickerClose} onClick={() => setShowPicker(false)}>✕</button>
            </div>
            <div style={styles.pickerDivider} />

            {friends.length === 0 ? (
              <div style={styles.pickerEmpty}>
                <p style={styles.pickerEmptyTitle}>NO FRIENDS YET</p>
                <p style={styles.pickerEmptySub}>Add friends from the friends screen to challenge them.</p>
              </div>
            ) : (
              <div style={styles.pickerList}>
                {friends.map((f) => {
                  const atLimit = sent.length >= 10;
                  const already = sent.some((c) => c.toUID === f.id && c.sport === selectedSport);
                  const disabled = atLimit || already;
                  const reason = already ? "Challenge sent" : atLimit ? "Challenge limit reached" : null;
                  return (
                    <button
                      key={f.id}
                      style={{ ...styles.pickerRow, ...(disabled ? styles.pickerRowDisabled : {}) }}
                      onClick={() => !disabled && challengeFriend(f)}
                      disabled={disabled}
                    >
                      <span style={{ ...styles.pickerAvatar, opacity: disabled ? 0.4 : 1 }}>
                        {f.username.charAt(0).toUpperCase()}
                      </span>
                      <span style={styles.pickerNameWrap}>
                        <span style={{ ...styles.pickerName, color: disabled ? "rgba(255,255,255,0.3)" : "#fff" }}>
                          {f.username}
                        </span>
                        {reason && <span style={styles.pickerReason}>{reason}</span>}
                      </span>
                      {!disabled && <span style={styles.pickerChevron}>›</span>}
                    </button>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* CPU difficulty picker — centered modal (matches the app's other modals). Pick a
          difficulty to start a solo game vs the computer in the selected sport. */}
      {showCpuPicker && (
        <div style={styles.cpuOverlay} onClick={() => setShowCpuPicker(false)}>
          <div style={styles.cpuModal} onClick={(e) => e.stopPropagation()}>
            <div style={styles.cpuModalHeader}>
              <span style={maskStyle(meta.icon, meta.hex, 16)} />
              <span style={styles.cpuModalTitle}>PLAY CPU</span>
            </div>
            <div style={styles.cpuModalOptions}>
              {Object.values(DIFFICULTIES).map((d) => (
                <button
                  key={d.id}
                  style={{ ...styles.cpuModalBtn, background: `${meta.hex}14`, borderColor: `${meta.hex}40` }}
                  onClick={() => navigate(`/battles/pickup/cpu?sport=${selectedSport}&difficulty=${d.id}`)}
                >
                  {d.label}
                </button>
              ))}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

function TitleRow() {
  return (
    <div style={styles.titleRow}>
      <span style={maskStyle(strategyIcon, TEAL, 24)} />
      <span style={styles.title}>PICK-UP DRAFT</span>
      <span style={maskStyle(strategyIcon, TEAL, 24)} />
    </div>
  );
}

function SectionHeader({ label, color }) {
  return (
    <div style={styles.sectionHeader}>
      <span style={{ ...styles.sectionLine, background: `linear-gradient(to left, ${color}8c, transparent)` }} />
      <span style={{ ...styles.sectionLabel, color }}>{label}</span>
      <span style={{ ...styles.sectionLine, background: `linear-gradient(to right, ${color}8c, transparent)` }} />
    </div>
  );
}

function Rule({ hex, text }) {
  return (
    <div style={styles.rule}>
      <span style={{ ...styles.ruleDot, background: hex }} />
      <span style={styles.ruleText}>{text}</span>
    </div>
  );
}

function Avatar({ initial }) {
  return <span style={styles.avatar}>{initial}</span>;
}

function IncomingChallengeRow({ challenge, hex, accepting, onAccept, onDecline }) {
  return (
    <div style={{ ...styles.chalRow, background: `${hex}0d`, borderColor: `${hex}26` }}>
      <Avatar initial={challenge.fromUsername.charAt(0).toUpperCase()} />
      <span style={styles.chalText}>
        <span style={styles.chalName}>{challenge.fromUsername}</span>
        <span style={styles.chalSub}>challenged you</span>
      </span>
      <button
        style={{ ...styles.circleBtn, background: hex, color: "#0c0d10", opacity: accepting ? 0.6 : 1 }}
        onClick={onAccept}
        disabled={accepting}
      >
        ✓
      </button>
      <button style={styles.circleGhost} onClick={onDecline}>✕</button>
    </div>
  );
}

function SentChallengeRow({ challenge, onCancel }) {
  return (
    <div style={{ ...styles.chalRow, background: "rgba(255,255,255,0.03)", borderColor: "rgba(255,255,255,0.08)" }}>
      <Avatar initial={challenge.toUsername.charAt(0).toUpperCase()} />
      <span style={styles.chalText}>
        <span style={styles.chalName}>{challenge.toUsername}</span>
        <span style={styles.chalSub}>challenge sent</span>
      </span>
      <button style={styles.circleGhost} onClick={onCancel}>✕</button>
    </div>
  );
}

const styles = {
  page: { padding: "8px 14px 16px", maxWidth: 560, margin: "0 auto", color: "#e8e9ec", minHeight: "calc(100vh - 190px)", display: "flex", flexDirection: "column" },
  headerRow: { display: "flex", alignItems: "center", paddingTop: 6, paddingBottom: 8 },
  iconBtn: { background: "none", border: "none", color: "rgba(255,255,255,0.8)", fontSize: 28, cursor: "pointer", width: 32, lineHeight: 1, textAlign: "left" },
  titleCenter: { flex: 1, display: "flex", alignItems: "center", justifyContent: "center", gap: 8, minWidth: 0, overflow: "hidden" },
  friendsBtn: { background: "none", border: "none", cursor: "pointer", width: 32, display: "flex", justifyContent: "flex-end" },
  friendsDot: { position: "absolute", top: -2, right: -2, width: 8, height: 8, borderRadius: 4, background: APP_THEME },
  titleRow: { display: "flex", alignItems: "center", justifyContent: "center", gap: 10, padding: "12px 0 10px" },
  title: { fontSize: 20, fontWeight: 900, fontStyle: "italic", letterSpacing: 1.5, color: "#fff", whiteSpace: "nowrap" },
  sportRow: { display: "flex", gap: 8 },
  sportTile: { position: "relative", flex: 1, height: 48, borderRadius: 12, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", transition: "transform 0.2s" },
  sportDot: { position: "absolute", top: 7, right: 9, width: 6, height: 6, borderRadius: 3 },
  recordCard: { display: "flex", alignItems: "center", border: "1px solid", borderRadius: 14, padding: "10px 0", margin: "12px 0" },
  recordCol: { flex: 1, display: "flex", flexDirection: "column", alignItems: "center", gap: 4 },
  recordValue: { fontSize: 26, fontWeight: 700, color: "#fff" },
  recordLabel: { fontSize: 10, fontWeight: 700, letterSpacing: 1, color: "rgba(255,255,255,0.45)" },
  recordDivider: { width: 1, height: 40, background: "rgba(255,255,255,0.1)" },
  scroll: { flex: 1 },
  block: { marginBottom: 12 },
  gameList: { display: "flex", flexDirection: "column", gap: 8, marginTop: 8 },
  sectionHeader: { display: "flex", alignItems: "center", gap: 10, marginBottom: 4 },
  sectionLine: { flex: 1, height: 1 },
  sectionLabel: { fontSize: 12, fontWeight: 700, letterSpacing: 2.5, whiteSpace: "nowrap" },
  rule: { display: "flex", alignItems: "center", gap: 10, marginTop: 10 },
  ruleDot: { width: 7, height: 7, borderRadius: 4, opacity: 0.7, flexShrink: 0 },
  ruleText: { fontSize: 13, color: "rgba(255,255,255,0.6)" },
  rulesCard: { background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: 16, marginTop: 4 },
  chalRow: { display: "flex", alignItems: "center", gap: 12, border: "1px solid", borderRadius: 12, padding: 10, marginTop: 8 },
  chalText: { display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 },
  chalName: { fontSize: 15, fontWeight: 800, color: "#fff", whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  chalSub: { fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.4)" },
  circleBtn: { width: 34, height: 34, borderRadius: 17, border: "none", fontWeight: 800, fontSize: 14, cursor: "pointer", flexShrink: 0 },
  circleGhost: { width: 34, height: 34, borderRadius: 17, border: "1px solid rgba(255,255,255,0.12)", background: "rgba(255,255,255,0.08)", color: "rgba(255,255,255,0.5)", fontWeight: 800, fontSize: 13, cursor: "pointer", flexShrink: 0 },
  avatar: { width: 38, height: 38, borderRadius: 19, background: "rgba(255,255,255,0.08)", border: "1px solid rgba(255,255,255,0.12)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 15, fontWeight: 800, color: "rgba(255,255,255,0.7)", flexShrink: 0 },
  error: { fontSize: 12, fontWeight: 500, color: "rgba(255,90,90,0.8)", textAlign: "center", marginTop: 8 },
  pinned: { display: "flex", flexDirection: "column", gap: 10, marginTop: 14, paddingTop: 10, borderTop: "1px solid rgba(255,255,255,0.07)" },
  pinnedRow: { display: "flex", gap: 10 },
  cpuBtn: { height: 50, borderRadius: 14, border: "none", color: "#fff", fontWeight: 800, fontSize: 13, letterSpacing: 0.8, cursor: "pointer" },
  tealBtn: { flex: 1, height: 50, borderRadius: 14, border: "none", background: `linear-gradient(to right, ${TEAL}, ${TEAL}cc)`, color: "#fff", fontWeight: 800, fontSize: 12, letterSpacing: 0.3, cursor: "pointer", boxShadow: `0 5px 10px ${TEAL}66` },
  cpuOverlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", backdropFilter: "blur(3px)", display: "flex", alignItems: "center", justifyContent: "center", padding: 24, zIndex: 200 },
  cpuModal: { width: "100%", maxWidth: 320, background: "rgb(20,20,32)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 20, padding: "22px 20px", boxShadow: "0 24px 60px rgba(0,0,0,0.6)", display: "flex", flexDirection: "column", alignItems: "center" },
  cpuModalHeader: { display: "flex", alignItems: "center", justifyContent: "center", gap: 8, marginBottom: 18 },
  cpuModalTitle: { fontSize: 16, fontWeight: 900, fontStyle: "italic", letterSpacing: 1.5, color: "#fff" },
  cpuModalOptions: { display: "flex", flexDirection: "column", gap: 10, width: "100%" },
  cpuModalBtn: { width: "100%", padding: "15px", borderRadius: 14, border: "1px solid", color: "#fff", fontSize: 15, fontWeight: 800, letterSpacing: 2, cursor: "pointer", textAlign: "center" },
  primaryBtn: { marginTop: 12, padding: "12px 18px", borderRadius: 10, border: "none", background: APP_THEME, color: "#0c0d10", fontWeight: 800, letterSpacing: 1, cursor: "pointer" },
  dim: { fontSize: 13, color: "#6e747e" },
  overlay: { position: "fixed", inset: 0, background: "rgba(0,0,0,0.6)", display: "flex", alignItems: "flex-end", justifyContent: "center", zIndex: 50 },
  sheet: { width: "100%", maxWidth: 560, background: "#12151f", borderRadius: "16px 16px 0 0", padding: 16, maxHeight: "70vh", overflowY: "auto" },
  pickerHeader: { display: "flex", alignItems: "center", justifyContent: "space-between", paddingBottom: 14 },
  pickerTitleWrap: { display: "flex", flexDirection: "column", alignItems: "center", gap: 3, flex: 1 },
  pickerTitle: { fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: "#fff" },
  pickerSportRow: { display: "flex", alignItems: "center", gap: 5 },
  pickerSportName: { fontSize: 10, fontWeight: 700, letterSpacing: 1 },
  pickerClose: { width: 28, background: "none", border: "none", color: "rgba(255,255,255,0.5)", fontSize: 14, cursor: "pointer" },
  pickerDivider: { height: 1, background: "rgba(255,255,255,0.06)", marginBottom: 14 },
  pickerList: { display: "flex", flexDirection: "column", gap: 8 },
  pickerRow: { display: "flex", alignItems: "center", gap: 12, width: "100%", textAlign: "left", padding: 12, borderRadius: 14, background: "#16181d", border: "1px solid rgba(255,255,255,0.07)", cursor: "pointer" },
  pickerRowDisabled: { background: "rgba(255,255,255,0.02)", border: "1px solid rgba(255,255,255,0.04)", cursor: "default" },
  pickerAvatar: { width: 40, height: 40, borderRadius: 20, background: `${APP_THEME}1a`, border: `1px solid ${APP_THEME}33`, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, fontWeight: 800, color: APP_THEME, flexShrink: 0 },
  pickerNameWrap: { display: "flex", flexDirection: "column", gap: 2, flex: 1, minWidth: 0 },
  pickerName: { fontSize: 14, fontWeight: 800, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" },
  pickerReason: { fontSize: 10, fontWeight: 500, color: "rgba(255,255,255,0.3)" },
  pickerChevron: { fontSize: 18, fontWeight: 700, color: "rgba(255,255,255,0.25)", flexShrink: 0 },
  pickerEmpty: { textAlign: "center", padding: "30px 16px" },
  pickerEmptyTitle: { fontSize: 13, fontWeight: 800, letterSpacing: 1.5, color: "rgba(255,255,255,0.3)", margin: "0 0 6px" },
  pickerEmptySub: { fontSize: 12, fontWeight: 500, color: "rgba(255,255,255,0.22)", lineHeight: 1.5, maxWidth: 280, margin: "0 auto" },
};
